<template>
    <div>
  
      <!-- Page Header -->
      <div class="page-header">
        <div>
          <h5 class="page-title">
            {{ details[0]?.debtor?.customer?.customer_name || 'Debtor Details' }}
          </h5>
          <p class="page-sub">Sales debt history for this customer</p>
        </div>
        <div class="page-actions">
  
          <!-- Back -->
          <router-link :to="$url('/debtor')" class="btn-outline">
            <font-awesome-icon icon="arrow-left" /> Back
          </router-link>
  
          <!-- Date Range -->
          <div class="date-range">
            <div class="date-field">
              <font-awesome-icon icon="calendar" class="date-icon" />
              <Datepicker v-model="tableData.start_date" :enableTimePicker="false" class="date-picker" placeholder="Start date" />
            </div>
            <span class="date-sep">—</span>
            <div class="date-field">
              <font-awesome-icon icon="calendar" class="date-icon" />
              <Datepicker v-model="tableData.end_date" :minDate="tableData.start_date" :enableTimePicker="false" class="date-picker" placeholder="End date" />
            </div>
            <button class="btn-primary-action" @click="fetchData()" :disabled="crudLoading">
              <span v-if="crudLoading" class="spinner-border spinner-border-sm me-1" role="status"></span>
              <font-awesome-icon v-else icon="search" class="me-1" />
              Filter
            </button>
          </div>
  
          <!-- Search -->
          <div class="search-wrap">
            <font-awesome-icon icon="search" class="search-icon" />
            <input type="text" class="search-input" placeholder="Search..." v-model="query" />
          </div>
  
          <!-- Export -->
          <div class="dropdown">
            <button class="btn-outline dropdown-toggle" data-bs-toggle="dropdown" aria-expanded="false">
              <font-awesome-icon icon="download" /> Export
            </button>
            <ul class="dropdown-menu dropdown-menu-end">
              <li><a class="dropdown-item" href="#" @click="drawPdf(print_content, headerArrayColumn, 'l', customerName, customerName)">
                <font-awesome-icon icon="file-pdf" class="me-2 text-danger" /> PDF
              </a></li>
              <li><a class="dropdown-item" href="#" @click="reusableMixinToCSV('User-debtors.csv')">
                <font-awesome-icon icon="file-csv" class="me-2 text-success" /> CSV
              </a></li>
            </ul>
          </div>
  
        </div>
      </div>
  
      <!-- Data Card -->
      <div class="data-card">
  
        <!-- Loading -->
        <div v-if="loading" class="state-block">
          <div class="spinner-border text-primary" style="width:3rem;height:3rem;" role="status"></div>
          <p class="mt-3 text-muted">Loading debt records...</p>
        </div>
  
        <!-- Empty -->
        <div v-else-if="details.length === 0" class="state-block">
          <div class="state-icon state-icon--green">
            <font-awesome-icon icon="check-circle" />
          </div>
          <p class="state-title">No debt records found</p>
          <p class="state-sub">This customer has no outstanding debts in the selected period.</p>
        </div>
  
        <!-- Table -->
        <template v-else>
  
          <div class="table-toolbar">
            <span class="table-count">
              Showing <strong>{{ pagination.from }}–{{ pagination.to }}</strong> of <strong>{{ pagination.total }}</strong> records
            </span>
          </div>
  
          <div class="table-wrap">
            <table class="data-table">
              <thead>
                <tr>
                  <th>Sale No.</th>
                  <th>Last Payment</th>
                  <th class="text-right">Total ({{ company?.currency?.symbol }})</th>
                  <th class="text-right">Received ({{ company?.currency?.symbol }})</th>
                  <th class="text-right">Balance ({{ company?.currency?.symbol }})</th>
                  <th>Status</th>
                  <th class="col-actions">Action</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="debt in details" :key="debt.id">
  
                  <td>
                    <span class="batch-code">{{ debt?.sales?.batch_code }}</span>
                  </td>
  
                  <td>
                    <p class="date-primary">{{ dateTime(debt?.last_payment) }}</p>
                    <p class="date-secondary">{{ dateTime(debt?.last_payment, 'days ago') }}</p>
                  </td>
  
                  <td class="text-right text-muted-cell">
                    {{ $filters.formatMoney(debt?.total_amount) }}
                  </td>
  
                  <td class="text-right text-muted-cell">
                    {{ $filters.formatMoney(debt?.amount_paid) }}
                  </td>
  
                  <td class="text-right">
                    <span class="balance-badge" :class="parseFloat(debt?.amount_owned) > 0 ? 'balance-badge--due' : 'balance-badge--clear'">
                      {{ $filters.formatMoney(debt?.amount_owned) }}
                    </span>
                  </td>
  
                  <td>
                    <span class="overdue-badge">
                      <font-awesome-icon icon="clock" class="me-1" />
                      {{ dateTime(debt?.last_payment, 'days ago') }}
                    </span>
                  </td>
  
                  <td class="col-actions">
                    <router-link
                      :to="$url('/debtor-receipt/' + debt?.id)"
                      class="action-btn action-btn--view"
                      title="View receipt"
                    >
                      <font-awesome-icon icon="eye" />
                    </router-link>
                  </td>
  
                </tr>
              </tbody>
            </table>
          </div>
  
          <div class="table-footer">
            <pagination
              :pagination="pagination"
              @prev="tableData.page = pagination.currentPage - 1; fetchData()"
              @next="tableData.page = pagination.currentPage + 1; fetchData()"
              v-if="details.length > 0"
            />
          </div>
  
        </template>
      </div>
  
    </div>
  </template>
  
  <script>
  import axios from 'axios';
  import { useToast } from 'vue-toastification';
  import { reusableMixin } from '../../../lib/reusableMixin';
  
  export default {
    mixins: [reusableMixin],
  
    data() {
      const sixMonthsAgo = new Date();
      sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 6);
  
      return {
        print_content: [],
        headerArrayColumn: ['S/N','Sale No.','Date','Amount Paid','Balance'],
        details: [], loading: false, query: null,
        selectedModel: [], allSelected: false,
        tableData: { page: 1,
          start_date: sixMonthsAgo, end_date: new Date(),
          draw: 0, length: 10, search: '', column: 0,
          dir: 'desc', id: '', status: [0,1,2,3], query: '',
        },
        pagination: { lastPage:'', currentPage:'', total:'', lastPageUrl:'', nextPageUrl:'', prevPageUrl:'', from:'', to:'' },
        toast: useToast(), permission_status: null, crudLoading: false,
      };
    },
  
    computed: {
      customerName() {
        return this.details[0]?.debtor?.customer?.customer_name ?? 'Customer';
      },
    },
  
    watch: { query() { this.fetchData(); } },
  
    mounted() { this.fetchData(); this.getCompany(); },
  
    methods: {
      dataToPrint(details) {
        this.print_content = details.map((debt, idx) => ({
          sn:           idx + 1,
          batch_code:   debt?.sales?.batch_code,
          date:         this.dateTime(debt?.last_payment),
          amount_paid:  debt?.amount_paid,
          amount_owned: debt?.amount_owned,
        }));
      },
  
      fetchData(url = `/debtors/customer/${this.$route.params.id}`) {
        this.loading = true; this.crudLoading = true;
        this.tableData.query = this.query;
        this.tableData.draw++;
        axios.get(url, { params: this.tableData })
          .then(response => {
            this.crudLoading = false; this.loading = false;
            this.details = response.data.data.data;
            this.dataToPrint(this.details);
            this.configPagination(response.data.data);
          })
          .catch(error => {
            this.crudLoading = false; this.loading = false;
            if (error?.response?.status == 403) {
              this.permission_status = 403;
              this.toast.error(error.response.data.message);
            }
          });
      },
  
      configPagination(data) {
        this.pagination.lastPage    = data.last_page;
        this.pagination.currentPage = data.current_page;
        this.pagination.total       = data.total;
        this.pagination.lastPageUrl = data.last_page_url;
        this.pagination.nextPageUrl = data.next_page_url;
        this.pagination.prevPageUrl = data.prev_page_url;
        this.pagination.from        = data.from;
        this.pagination.to          = data.to;
      },
    },
  };
  </script>
  
  <style scoped>
  /* ── Page Header ── */
  .page-header  { display:flex; align-items:flex-start; justify-content:space-between; flex-wrap:wrap; gap:14px; margin-bottom:20px; }
  .page-title   { font-size:20px; font-weight:700; color:#1a2b45; margin:0 0 3px; }
  .page-sub     { font-size:13px; color:#7a90b0; margin:0; }
  .page-actions { display:flex; align-items:center; flex-wrap:wrap; gap:8px; }
  
  /* ── Date Range ── */
  .date-range { display:flex; align-items:center; gap:6px; background:#fff; border:1px solid #dde5f0; border-radius:8px; padding:4px 10px; }
  .date-field  { position:relative; display:flex; align-items:center; }
  .date-icon   { position:absolute; left:8px; color:#7a90b0; font-size:12px; z-index:2; pointer-events:none; }
  :deep(.date-picker) { border:none !important; background:transparent !important; box-shadow:none !important; font-size:13px; width:130px; padding-left:22px !important; }
  :deep(.date-picker input) { border:none !important; font-size:13px; color:#1a2b45; }
  .date-sep    { color:#aab8cc; font-size:13px; }
  
  /* ── Search ── */
  .search-wrap  { position:relative; display:flex; align-items:center; }
  .search-icon  { position:absolute; left:11px; color:#7a90b0; font-size:13px; pointer-events:none; }
  .search-input { padding:8px 12px 8px 32px; border:1px solid #dde5f0; border-radius:8px; font-size:13px; color:#1a2b45; background:#fff; width:180px; outline:none; transition:border-color .15s; }
  .search-input:focus { border-color:#004AAD; }
  .search-input::placeholder { color:#aab8cc; }
  
  /* ── Buttons ── */
  .btn-primary-action { display:inline-flex; align-items:center; gap:7px; background:#004AAD; color:#fff; font-size:13px; font-weight:600; padding:8px 16px; border-radius:8px; border:none; text-decoration:none; cursor:pointer; transition:background .15s; white-space:nowrap; }
  .btn-primary-action:hover { background:#003b8e; color:#fff; }
  .btn-primary-action:disabled { opacity:.6; cursor:not-allowed; }
  .btn-outline { display:inline-flex; align-items:center; gap:7px; background:#fff; color:#1a2b45; border:1px solid #dde5f0; font-size:13px; font-weight:500; padding:8px 14px; border-radius:8px; cursor:pointer; transition:background .15s; white-space:nowrap; text-decoration:none; }
  .btn-outline:hover { background:#f4f7fb; color:#1a2b45; }
  .btn-outline::after { display:none !important; }
  
  /* ── Data Card ── */
  .data-card { background:#fff; border:1px solid #e4eaf2; border-radius:12px; overflow:hidden; }
  
  /* ── States ── */
  .state-block { display:flex; flex-direction:column; align-items:center; justify-content:center; padding:64px 24px; text-align:center; }
  .state-icon  { width:60px; height:60px; border-radius:16px; display:flex; align-items:center; justify-content:center; font-size:24px; margin-bottom:16px; }
  .state-icon--green { background:rgba(22,163,74,.1); color:#16a34a; }
  .state-title { font-size:16px; font-weight:700; color:#1a2b45; margin:0 0 6px; }
  .state-sub   { font-size:13px; color:#7a90b0; margin:0; }
  .mt-3        { margin-top:12px; }
  
  /* ── Toolbar ── */
  .table-toolbar { display:flex; align-items:center; justify-content:space-between; padding:14px 20px; border-bottom:1px solid #e4eaf2; flex-wrap:wrap; gap:10px; }
  .table-count   { font-size:13px; color:#7a90b0; }
  
  /* ── Table ── */
  .table-wrap  { overflow-x:auto; }
  .data-table  { width:100%; border-collapse:collapse; }
  .data-table thead tr { background:#f8fafc; }
  .data-table thead th { font-size:11.5px; font-weight:700; text-transform:uppercase; letter-spacing:.05em; color:#7a90b0; padding:12px 16px; border-bottom:1px solid #e4eaf2; white-space:nowrap; }
  .data-table tbody tr { border-bottom:1px solid #f0f4f8; transition:background .12s; }
  .data-table tbody tr:last-child { border-bottom:none; }
  .data-table tbody tr:hover { background:#f8fafc; }
  .data-table tbody td { padding:12px 16px; font-size:13.5px; vertical-align:middle; }
  .col-actions { width:60px; text-align:center; }
  .text-right      { text-align:right; }
  .text-muted-cell { color:#4a5e78; }
  
  /* ── Batch Code ── */
  .batch-code { font-family:monospace; font-size:13px; font-weight:600; color:#1a2b45; background:#f4f7fb; border:1px solid #e4eaf2; border-radius:6px; padding:3px 10px; }
  
  /* ── Date ── */
  .date-primary   { font-size:13.5px; font-weight:600; color:#1a2b45; margin:0 0 2px; }
  .date-secondary { font-size:12px; color:#7a90b0; margin:0; }
  
  /* ── Balance Badge ── */
  .balance-badge      { display:inline-block; border-radius:6px; padding:3px 10px; font-size:13px; font-weight:700; border:1px solid transparent; }
  .balance-badge--due  { background:#fff0f0; color:#e03535; border-color:#fac5c5; }
  .balance-badge--clear { background:#f0fdf4; color:#16a34a; border-color:#bbf7d0; }
  
  /* ── Overdue Badge ── */
  .overdue-badge { display:inline-flex; align-items:center; background:#fff7ed; color:#e08c00; border:1px solid #fed7aa; border-radius:6px; padding:3px 10px; font-size:12px; font-weight:600; }
  
  /* ── Action Button ── */
  .action-btn  { width:32px; height:32px; border-radius:7px; border:1px solid #e4eaf2; display:flex; align-items:center; justify-content:center; font-size:13px; cursor:pointer; transition:background .15s; text-decoration:none; background:#fff; margin:0 auto; }
  .action-btn--view { color:#0d9488; }
  .action-btn--view:hover { background:#f0fdfb; border-color:#a7f3d0; text-decoration:none; }
  
  /* ── Footer ── */
  .table-footer { padding:14px 20px; border-top:1px solid #e4eaf2; display:flex; justify-content:flex-end; }
  
  /* ── Dropdown ── */
  .dropdown-menu { border:1px solid #e4eaf2; border-radius:9px; box-shadow:0 8px 24px rgba(0,0,0,.08); padding:6px; min-width:170px; }
  .dropdown-item { border-radius:6px; font-size:13px; padding:8px 12px; color:#1a2b45; }
  .dropdown-item:hover { background:#f4f7fb; }
  
  /* ── Responsive ── */
  @media(max-width:991px) { .date-range { flex-wrap:wrap; } }
  @media(max-width:767px) {
    .page-header,.page-actions { flex-direction:column; align-items:stretch; }
    .search-input { width:100%; }
    .btn-primary-action,.btn-outline { width:100%; justify-content:center; }
    .date-range { width:100%; }
  }
  </style>